import React, { useState, useEffect, useCallback } from 'react';
import { BarChart2, TrendingUp, Flame, User, Download, Users, CalendarOff, UserCheck, BriefcaseMedical, Umbrella, Trash2, X, Clock, ChevronLeft, ChevronRight, Pencil, RotateCcw, Check, FolderOpen } from 'lucide-react';
import { api } from '../api';
import DocumentsEmployes from './DocumentsEmployes';
import { useConfirm } from '../components/ConfirmModal';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend
} from 'recharts';

const card = { background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', padding: '20px 24px' };
const COLORS = ['#1a56db', '#16a34a', '#f59e0b', '#dc2626', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];
const API = 'https://qbdp-backend-production.up.railway.app/api';

export default function Rapports() {
  const [employes, setEmployes]       = useState([]);
  const [clients, setClients]         = useState([]);
  const [logs, setLogs]               = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('employes');
  const [dateDebut, setDateDebut]     = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; });
  const [dateFin, setDateFin]         = useState(() => new Date().toISOString().split('T')[0]);
  const [allAbsences, setAllAbsences] = useState([]);
  const [services, setServices]       = useState([]);
  const [veille, setVeille]           = useState([]);
  const [absModal, setAbsModal]       = useState(null);
  const [absForm, setAbsForm]         = useState({ date_debut: '', date_fin: '', type: 'conge', commentaire: '' });
  const [absSaving, setAbsSaving]     = useState(false);
  const [search, setSearch]           = useState('');
  const [heuresModal, setHeuresModal]   = useState(null);
  const [absListModal, setAbsListModal] = useState(null);
  const [veilleFilter, setVeilleFilter] = useState(null);
  const { showConfirm, confirmModal } = useConfirm();

  const loadGestion = () => {
    Promise.all([api.getEmployes(), api.getAllAbsences(), api.getVeille()])
      .then(([emp, abs, vei]) => { setEmployes(emp); setAllAbsences(abs); setVeille(vei); })
      .catch(console.error);
  };

  useEffect(() => {
    Promise.all([api.getEmployes(), api.getClients(), api.getLogs({ limit: 1000 }), api.getStats(), api.getAllAbsences(), api.getServices(), api.getVeille()])
      .then(([emp, cli, lg, st, abs, svc, vei]) => { setEmployes(emp); setClients(cli); setLogs(lg); setStats(st); setAllAbsences(abs); setServices(svc); setVeille(vei); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const token = sessionStorage.getItem('qbdp_token');

  const exportCSV = async (route, filename) => {
    const r = await fetch(`${API}/export/${route}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Stats par service
  const statsByService = employes.reduce((acc, e) => {
    const s = e.denomination || e.service || 'Non défini';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const pieData = Object.entries(statsByService).map(([name, value]) => ({ name, value }));

  // Logs filtrés par période
  const logsFiltered = logs.filter(l => {
    const d = new Date(l.created_at);
    return d >= new Date(dateDebut) && d <= new Date(dateFin + 'T23:59:59');
  });

  // Évolution sur 30 jours
  const evolutionData = (() => {
    const days = {};
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const key = d.toISOString().split('T')[0];
      days[key] = 0;
    }
    logs.forEach(l => {
      const key = new Date(l.created_at).toISOString().split('T')[0];
      if (key in days) days[key]++;
    });
    return Object.entries(days).map(([date, count]) => ({
      date: new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      accès: count
    }));
  })();

  // Heatmap par heure et jour
  const heatmapData = (() => {
    const jours = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
    logs.forEach(l => {
      const d = new Date(l.created_at);
      const jour = (d.getDay() + 6) % 7; // 0=Lun
      const heure = d.getHours();
      grid[jour][heure]++;
    });
    const max = Math.max(...grid.flat(), 1);
    return { grid, jours, max };
  })();

  // Stats par employé
  const statsByEmploye = (() => {
    const acc = {};
    logs.forEach(l => {
      if (l.employe) {
        acc[l.employe] = (acc[l.employe] || 0) + 1;
      }
    });
    return Object.entries(acc)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([nom, count]) => ({ nom, count }));
  })();

  // Stats par zone
  const statsByZone = (() => {
    const acc = {};
    logs.forEach(l => {
      if (l.porte) {
        const key = l.batiment ? `${l.batiment} — ${l.porte}` : l.porte;
        acc[key] = (acc[key] || 0) + 1;
      }
    });
    return Object.entries(acc)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([zone, count]) => ({ zone, count }));
  })();

  const today = new Date(); today.setHours(0,0,0,0);
  const isActiveAbs = (a) => { const d = new Date(a.date_debut); const f = new Date(a.date_fin); return d <= today && today <= f; };
  const isFutureAbs = (a) => new Date(a.date_debut) > today;

  const absActives  = allAbsences.filter(isActiveAbs);
  const absFutures  = allAbsences.filter(isFutureAbs).slice(0, 10);

  const nbPresents  = employes.filter(e => e.statut === 'actif' && !e.en_conge).length;
  const nbConge     = employes.filter(e => e.en_conge && allAbsences.some(a => isActiveAbs(a) && a.employe_id === e.id && a.type === 'conge')).length;
  const nbMaladie   = employes.filter(e => e.en_conge && allAbsences.some(a => isActiveAbs(a) && a.employe_id === e.id && a.type === 'maladie')).length;
  const nbInactifs  = employes.filter(e => e.statut === 'inactif').length;

  // Veille — computed from badge data
  const veilleById   = Object.fromEntries(veille.map(v => [v.id, v]));
  const vNbPresent   = veille.filter(v => v.heure_arrivee && !v.heure_depart).length;
  const vNbParti     = veille.filter(v => !!v.heure_depart).length;
  const vNbPasEncore = veille.filter(v => !v.heure_arrivee).length;

  const TYPE_LABELS = { conge: 'Congé', maladie: 'Maladie', formation: 'Formation', autre: 'Autre' };
  const TYPE_COLORS = { conge: { bg: '#fef3c7', color: '#d97706', border: '#fde68a' }, maladie: { bg: '#fee2e2', color: '#dc2626', border: '#fecaca' }, formation: { bg: '#eff6ff', color: '#1a56db', border: '#bfdbfe' }, autre: { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' } };
  const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

  const handleAddAbsence = async () => {
    if (!absModal || !absForm.date_debut || !absForm.date_fin) return;
    setAbsSaving(true);
    try {
      await api.addAbsence(absModal.id, absForm);
      setAbsModal(null);
      setAbsForm({ date_debut: '', date_fin: '', type: 'conge', commentaire: '' });
      loadGestion();
    } catch (err) { alert(err.message); }
    setAbsSaving(false);
  };

  const handleDeleteAbsence = async (empId, absId, type) => {
    const ok = await showConfirm({
      title: 'SUPPRIMER CETTE ABSENCE',
      subtitle: type,
      body: 'Cette action est irréversible. L\'absence sera définitivement supprimée.',
      confirmLabel: 'Supprimer',
      confirmColor: '#dc2626',
      icon: Trash2,
    });
    if (!ok) return;
    try { await api.deleteAbsence(empId, absId); loadGestion(); } catch (err) { alert(err.message); }
  };

  const serviceOrder = services.map(s => s.nom);
  const searchLower  = search.toLowerCase();
  const employesFiltres = employes
    .filter(e => {
      if (searchLower && !e.nom.toLowerCase().includes(searchLower) &&
        !(e.service || '').toLowerCase().includes(searchLower) &&
        !(e.denomination || '').toLowerCase().includes(searchLower) &&
        !(e.matricule || '').toLowerCase().includes(searchLower)) return false;
      if (veilleFilter && veille.length > 0) {
        const v = veilleById[e.id];
        if (veilleFilter === 'present')    return v && v.heure_arrivee && !v.heure_depart;
        if (veilleFilter === 'parti')      return v && !!v.heure_depart;
        if (veilleFilter === 'pas_encore') return !v || !v.heure_arrivee;
      }
      return true;
    })
    .sort((a, b) => {
      const ia = serviceOrder.indexOf(a.service);
      const ib = serviceOrder.indexOf(b.service);
      if (ia !== ib) {
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      }
      return (a.nom || '').localeCompare(b.nom || '');
    });

  const tabIcons = { employes: UserCheck, documents: FolderOpen, stats: BarChart2, activite: TrendingUp, heatmap: Flame, export: Download };
  const tabs = [
    { id: 'employes',  label: 'Gestion RH' },
    { id: 'documents', label: 'Documents' },
    { id: 'stats',     label: 'Statistiques' },
    { id: 'activite',  label: 'Activité' },
    { id: 'heatmap',   label: 'Heatmap' },
    { id: 'export',    label: 'Exports' },
  ];

  if (loading) return <div style={{ padding: '28px 32px', color: 'var(--gray)', fontSize: 13 }}>Chargement...</div>;

  // ── Composant modal heures inline ──
  const HeuresModal = ({ employe, onClose }) => {
    const now = new Date();
    const [mois, setMois] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
    const [pointage, setPointage] = useState([]);
    const [loadingP, setLoadingP] = useState(true);
    const [editRow, setEditRow]   = useState(null);
    const [editForm, setEditForm] = useState({});

    const load = useCallback(() => {
      setLoadingP(true);
      api.getPointage(employe.id, mois).then(setPointage).catch(()=>{}).finally(()=>setLoadingP(false));
    }, [mois]);
    useEffect(() => { load(); }, [load]);

    const prevMois = () => {
      const [y, m] = mois.split('-').map(Number);
      const d = new Date(y, m-2, 1);
      setMois(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
    };
    const nextMois = () => {
      const [y, m] = mois.split('-').map(Number);
      const d = new Date(y, m, 1);
      setMois(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
    };

    const duree = (arr, dep) => {
      if (!arr || !dep) return null;
      const [ah, am] = arr.split(':').map(Number);
      const [dh, dm] = dep.split(':').map(Number);
      const mins = (dh*60+dm) - (ah*60+am);
      if (mins <= 0) return null;
      return `${Math.floor(mins/60)}h${String(mins%60).padStart(2,'0')}`;
    };
    const totalMins = pointage.reduce((acc, p) => {
      const arr = p.heure_arrivee?.slice(0,5);
      const dep = p.heure_depart?.slice(0,5);
      if (!arr || !dep) return acc;
      const [ah,am] = arr.split(':').map(Number);
      const [dh,dm] = dep.split(':').map(Number);
      return acc + Math.max(0, (dh*60+dm)-(ah*60+am));
    }, 0);
    const totalStr = totalMins > 0 ? `${Math.floor(totalMins/60)}h${String(totalMins%60).padStart(2,'0')}` : '—';
    const joursPresents = pointage.filter(p => p.heure_arrivee).length;
    const moisLabel = new Date(`${mois}-15`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

    const startEdit = (p) => {
      setEditRow(new Date(p.date).toISOString().split('T')[0]);
      setEditForm({ heure_arrivee: p.heure_arrivee?.slice(0,5)||'', heure_depart: p.heure_depart?.slice(0,5)||'', commentaire: p.commentaire||'' });
    };
    const saveEdit = async (dateStr) => {
      await api.upsertPointage(employe.id, { date: dateStr, ...editForm });
      setEditRow(null); load();
    };
    const resetAuto = async (dateStr) => {
      await api.deletePointage(employe.id, dateStr);
      setEditRow(null); load();
    };

    const inp = { border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', fontSize: 12, outline: 'none', width: 80 };
    const today = new Date().toISOString().split('T')[0];

    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
        <div style={{ background:'white', borderRadius:16, padding:'22px 26px', width:680, maxHeight:'88vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.18)' }} onClick={e=>e.stopPropagation()}>

          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div>
              <div style={{ fontWeight:700, fontSize:16 }}>Heures — {employe.nom}</div>
              <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{employe.denomination || employe.service}</div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <button onClick={prevMois} style={{ background:'#f3f4f6', border:'none', borderRadius:6, padding:'5px 8px', cursor:'pointer' }}><ChevronLeft size={14}/></button>
              <span style={{ fontSize:13, fontWeight:600, minWidth:120, textAlign:'center', textTransform:'capitalize' }}>{moisLabel}</span>
              <button onClick={nextMois} style={{ background:'#f3f4f6', border:'none', borderRadius:6, padding:'5px 8px', cursor:'pointer' }}><ChevronRight size={14}/></button>
              <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', marginLeft:8 }}><X size={18} color="#6b7280"/></button>
            </div>
          </div>

          {/* KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
            {[
              { label:'Jours présents', val: joursPresents, color:'#16a34a' },
              { label:'Heures totales', val: totalStr,      color:'#1a56db' },
              { label:'Moy. / jour',    val: joursPresents > 0 ? `${Math.floor(totalMins/joursPresents/60)}h${String(Math.round(totalMins/joursPresents%60)).padStart(2,'0')}` : '—', color:'#7c3aed' },
            ].map(k => (
              <div key={k.label} style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 14px', textAlign:'center' }}>
                <div style={{ fontSize:20, fontWeight:800, color:k.color }}>{k.val}</div>
                <div style={{ fontSize:11, color:'#6b7280', marginTop:3 }}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* Table pointage */}
          {loadingP ? (
            <div style={{ textAlign:'center', padding:32, color:'#6b7280', fontSize:13 }}>Chargement…</div>
          ) : pointage.length === 0 ? (
            <div style={{ textAlign:'center', padding:32, color:'#6b7280', fontSize:13 }}>Aucune donnée pour ce mois</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid #e5e7eb' }}>
                  {['Date','Arrivée','Départ','Durée','Source','Commentaire',''].map(h => (
                    <th key={h} style={{ textAlign:'left', padding:'7px 6px', fontSize:11, color:'#6b7280', fontWeight:500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pointage.map(p => {
                  const dateStr = new Date(p.date).toISOString().split('T')[0];
                  const arr = p.heure_arrivee?.slice(0,5);
                  const dep = p.heure_depart?.slice(0,5);
                  const isToday = dateStr === today;
                  const isEdit  = editRow === dateStr;
                  return (
                    <tr key={dateStr} style={{ borderBottom:'1px solid #f3f4f6', background: isToday ? '#eff6ff' : 'white' }}>
                      <td style={{ padding:'8px 6px', fontSize:12, fontWeight: isToday ? 600 : 400, color: isToday ? '#1a56db' : '#374151' }}>
                        {new Date(p.date).toLocaleDateString('fr-FR', { weekday:'short', day:'2-digit', month:'2-digit' })}
                        {isToday && <span style={{ marginLeft:5, fontSize:9, background:'#1a56db', color:'white', borderRadius:4, padding:'1px 4px' }}>Auj.</span>}
                      </td>
                      {isEdit ? (
                        <>
                          <td style={{ padding:'6px' }}><input style={inp} type="time" value={editForm.heure_arrivee} onChange={e=>setEditForm(f=>({...f,heure_arrivee:e.target.value}))}/></td>
                          <td style={{ padding:'6px' }}><input style={inp} type="time" value={editForm.heure_depart}  onChange={e=>setEditForm(f=>({...f,heure_depart:e.target.value}))}/></td>
                          <td style={{ padding:'6px', fontSize:12, color:'#6b7280' }}>{duree(editForm.heure_arrivee, editForm.heure_depart) || '—'}</td>
                          <td/>
                          <td style={{ padding:'6px' }}><input style={{...inp, width:120}} placeholder="Note…" value={editForm.commentaire} onChange={e=>setEditForm(f=>({...f,commentaire:e.target.value}))}/></td>
                          <td style={{ padding:'6px' }}>
                            <div style={{ display:'flex', gap:4 }}>
                              <button onClick={()=>saveEdit(dateStr)} style={{ background:'#1a56db', border:'none', borderRadius:5, padding:'4px 6px', cursor:'pointer', display:'flex', alignItems:'center' }}><Check size={12} color="white"/></button>
                              <button onClick={()=>resetAuto(dateStr)} title="Revenir auto" style={{ background:'#f3f4f6', border:'none', borderRadius:5, padding:'4px 6px', cursor:'pointer', display:'flex', alignItems:'center' }}><RotateCcw size={12} color="#6b7280"/></button>
                              <button onClick={()=>setEditRow(null)} style={{ background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center' }}><X size={12} color="#6b7280"/></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding:'8px 6px', fontSize:12 }}>{arr || <span style={{color:'#d1d5db'}}>—</span>}</td>
                          <td style={{ padding:'8px 6px', fontSize:12 }}>{dep || <span style={{color:'#d1d5db'}}>—</span>}</td>
                          <td style={{ padding:'8px 6px', fontSize:12, fontWeight:600, color:'#1a56db' }}>{duree(arr,dep) || '—'}</td>
                          <td style={{ padding:'8px 6px' }}>
                            <span style={{ fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:8, background: p.source==='manuel' ? '#eff6ff' : '#f0fdf4', color: p.source==='manuel' ? '#1a56db' : '#16a34a' }}>{p.source==='manuel' ? 'Manuel' : 'Auto'}</span>
                          </td>
                          <td style={{ padding:'8px 6px', fontSize:11, color:'#6b7280', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.commentaire || '—'}</td>
                          <td style={{ padding:'8px 6px' }}>
                            <button onClick={()=>startEdit(p)} style={{ background:'none', border:'1px solid #e5e7eb', borderRadius:5, padding:'3px 6px', cursor:'pointer', display:'flex', alignItems:'center' }}><Pencil size={11} color="#6b7280"/></button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div style={{ fontSize:11, color:'#9ca3af', marginTop:12 }}>
            Auto = dérivé des badgeages porte • Manuel = saisi ou corrigé • ↺ remet en mode Auto
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600 }}>Gestion</h1>
        <p style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>Analyses, gestion RH et exports</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => {
          const Icon = tabIcons[t.id];
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: '9px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400,
              color: activeTab === t.id ? 'var(--blue)' : 'var(--gray)',
              borderBottom: activeTab === t.id ? '2px solid var(--blue)' : '2px solid transparent',
              marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6,
            }}><Icon size={14} />{t.label}</button>
          );
        })}
      </div>

      {/* ── DOCUMENTS ── */}
      {activeTab === 'documents' && (
        <div style={{ margin: '-28px -32px' }}>
          <DocumentsEmployes />
        </div>
      )}

      {/* ── STATISTIQUES ── */}
      {activeTab === 'stats' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[
              { val: employes.filter(e => e.statut === 'actif').length, label: 'Employés actifs', color: 'var(--blue)' },
              { val: clients.filter(c => c.statut === 'actif').length, label: 'Clients présents', color: '#16a34a' },
              { val: stats?.total || 0, label: 'Total accès', color: '#8b5cf6' },
              { val: stats?.today || 0, label: "Accès aujourd'hui", color: '#f59e0b' },
            ].map(k => (
              <div key={k.label} style={card}>
                <div style={{ fontSize: 28, fontWeight: 800, color: k.color }}>{k.val}</div>
                <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* Graphiques */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={card}>
              <div style={{ fontWeight: 600, marginBottom: 16 }}>Répartition par service</div>
              {pieData.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--gray)', padding: 40 }}>Aucun employé</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name} (${value})`} labelLine={false} style={{ fontSize: 10 }}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div style={card}>
              <div style={{ fontWeight: 600, marginBottom: 16 }}>Zones les plus actives</div>
              {statsByZone.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--gray)', padding: 40 }}>Aucun accès enregistré</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={statsByZone} layout="vertical" barSize={14}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="zone" tick={{ fontSize: 10 }} width={140} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#1a56db" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Type d'accès */}
          <div style={card}>
            <div style={{ fontWeight: 600, marginBottom: 16 }}>Types d'accès</div>
            <div style={{ display: 'flex', gap: 16 }}>
              {(stats?.byType || []).map(t => (
                <div key={t.type} style={{ background: t.type === 'success' ? '#f0fdf4' : t.type === 'error' ? '#fef2f2' : '#fffbeb', borderRadius: 10, padding: '12px 20px', flex: 1, textAlign: 'center', border: `1px solid ${t.type === 'success' ? '#bbf7d0' : t.type === 'error' ? '#fecaca' : '#fde68a'}` }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: t.type === 'success' ? '#16a34a' : t.type === 'error' ? '#dc2626' : '#d97706' }}>{t.count}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4, textTransform: 'capitalize' }}>{t.type}</div>
                </div>
              ))}
              {(!stats?.byType || stats.byType.length === 0) && (
                <div style={{ color: 'var(--gray)', fontSize: 13 }}>Aucun accès enregistré</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── ACTIVITÉ ── */}
      {activeTab === 'activite' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Filtres période */}
          <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px' }}>
            <span style={{ fontSize: 13, color: 'var(--gray)' }}>Période :</span>
            <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
              style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', fontSize: 13 }} />
            <span style={{ color: 'var(--gray)' }}>→</span>
            <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
              style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', fontSize: 13 }} />
            <span style={{ fontSize: 12, color: 'var(--gray)', marginLeft: 8 }}>{logsFiltered.length} accès</span>
          </div>

          {/* Évolution 30 jours */}
          <div style={card}>
            <div style={{ fontWeight: 600, marginBottom: 16 }}>Évolution des accès — 30 derniers jours</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={evolutionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="accès" stroke="#1a56db" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Tableau logs filtrés */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 600 }}>Historique ({logsFiltered.length} accès)</div>
            </div>
            {logsFiltered.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--gray)', padding: 40 }}>Aucun accès sur cette période</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Date', 'Employé', 'Zone', 'Type'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 0', fontSize: 11, color: 'var(--gray)', fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logsFiltered.slice(0, 50).map((l, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ padding: '8px 0', fontSize: 12, color: 'var(--gray)', paddingRight: 16 }}>{new Date(l.created_at).toLocaleString('fr-FR')}</td>
                      <td style={{ fontSize: 12, fontWeight: 500, paddingRight: 16 }}>{l.employe || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--gray)', paddingRight: 16 }}>{l.porte ? `${l.batiment} — ${l.porte}` : '—'}</td>
                      <td>
                        <span style={{ background: l.type === 'success' ? '#f0fdf4' : l.type === 'error' ? '#fef2f2' : '#fffbeb', color: l.type === 'success' ? '#16a34a' : l.type === 'error' ? '#dc2626' : '#d97706', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>
                          {l.type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── HEATMAP ── */}
      {activeTab === 'heatmap' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={card}>
            <div style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}><Flame size={15} color="#f59e0b" /> Heatmap des accès — par heure et jour</div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 20 }}>Plus la couleur est foncée, plus l'activité est intense</div>

            {logs.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--gray)', padding: 40 }}>Aucun accès enregistré</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {/* Heures */}
                <div style={{ display: 'flex', marginLeft: 40, marginBottom: 4 }}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} style={{ width: 28, textAlign: 'center', fontSize: 9, color: 'var(--gray)', flexShrink: 0 }}>
                      {h}h
                    </div>
                  ))}
                </div>
                {/* Grille */}
                {heatmapData.jours.map((jour, j) => (
                  <div key={jour} style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                    <div style={{ width: 36, fontSize: 11, color: 'var(--gray)', fontWeight: 500 }}>{jour}</div>
                    {Array.from({ length: 24 }, (_, h) => {
                      const val = heatmapData.grid[j][h];
                      const intensity = val / heatmapData.max;
                      const bg = intensity === 0 ? '#f3f4f6'
                        : intensity < 0.25 ? '#bfdbfe'
                        : intensity < 0.5  ? '#60a5fa'
                        : intensity < 0.75 ? '#2563eb'
                        : '#1e40af';
                      return (
                        <div key={h} title={`${jour} ${h}h : ${val} accès`}
                          style={{ width: 24, height: 24, background: bg, borderRadius: 4, marginRight: 4, flexShrink: 0, cursor: 'default', transition: 'transform .1s' }}
                          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.3)'}
                          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                        />
                      );
                    })}
                  </div>
                ))}
                {/* Légende */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
                  <span style={{ fontSize: 11, color: 'var(--gray)' }}>Moins</span>
                  {['#f3f4f6', '#bfdbfe', '#60a5fa', '#2563eb', '#1e40af'].map(c => (
                    <div key={c} style={{ width: 20, height: 20, background: c, borderRadius: 4 }} />
                  ))}
                  <span style={{ fontSize: 11, color: 'var(--gray)' }}>Plus</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── GESTION RH ── */}
      {activeTab === 'employes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Modal heures */}
          {heuresModal && <HeuresModal employe={heuresModal} onClose={() => setHeuresModal(null)} />}

          {/* Modal absences d'un employé */}
          {absListModal && (() => {
            const empAbs = allAbsences.filter(a => a.employe_id === absListModal.id).sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));
            return (
              <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setAbsListModal(null)}>
                <div style={{ background:'white', borderRadius:14, padding:'20px 24px', width:500, maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                    <div style={{ fontWeight:700, fontSize:15 }}>Absences — {absListModal.nom}</div>
                    <button onClick={() => setAbsListModal(null)} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={18} color="#6b7280"/></button>
                  </div>
                  {empAbs.length === 0 ? (
                    <div style={{ textAlign:'center', padding:32, color:'#6b7280', fontSize:13 }}>Aucune absence enregistrée</div>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {empAbs.map(a => {
                        const tc = TYPE_COLORS[a.type] || TYPE_COLORS.autre;
                        const active = isActiveAbs(a);
                        const future = isFutureAbs(a);
                        return (
                          <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:tc.bg, border:`1px solid ${tc.border}`, borderRadius:8, padding:'10px 14px' }}>
                            <div>
                              <div style={{ fontSize:13, fontWeight:500, display:'flex', alignItems:'center', gap:8 }}>
                                {fmt(a.date_debut)} → {fmt(a.date_fin)}
                                {active && <span style={{ fontSize:9, background:'#d97706', color:'white', borderRadius:4, padding:'1px 5px' }}>En cours</span>}
                                {future && <span style={{ fontSize:9, background:'#1a56db', color:'white', borderRadius:4, padding:'1px 5px' }}>À venir</span>}
                              </div>
                              <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>{TYPE_LABELS[a.type]}{a.commentaire ? ` — ${a.commentaire}` : ''}</div>
                            </div>
                            <button onClick={() => { handleDeleteAbsence(absListModal.id, a.id, TYPE_LABELS[a.type]); }} style={{ background:'none', border:'none', cursor:'pointer' }}><Trash2 size={13} color="#dc2626"/></button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid #f3f4f6' }}>
                    <button onClick={() => { setAbsListModal(null); setAbsModal(absListModal); setAbsForm({ date_debut:'', date_fin:'', type:'conge', commentaire:'' }); }}
                      style={{ background:'var(--blue)', color:'white', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                      + Ajouter une absence
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Veille du jour — barre compacte scalable */}
          {veille.length > 0 && (
            <div style={{ ...card, padding: '12px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', boxShadow: '0 0 0 3px #bbf7d0' }} />
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>Badgeages — temps réel</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{veille.length} employé{veille.length > 1 ? 's' : ''} suivi{veille.length > 1 ? 's' : ''}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[
                    { key: 'present',    nb: vNbPresent,   label: 'Présent',              dot: '#1a56db', bg: veilleFilter === 'present'    ? '#1a56db' : '#eff6ff', color: veilleFilter === 'present'    ? 'white' : '#1a56db', border: '#bfdbfe' },
                    { key: 'parti',      nb: vNbParti,     label: 'Parti',                dot: '#16a34a', bg: veilleFilter === 'parti'      ? '#16a34a' : '#f0fdf4', color: veilleFilter === 'parti'      ? 'white' : '#16a34a', border: '#bbf7d0' },
                    { key: 'pas_encore', nb: vNbPasEncore, label: 'Pas encore arrivé',   dot: '#d1d5db', bg: veilleFilter === 'pas_encore' ? '#6b7280' : '#f9fafb', color: veilleFilter === 'pas_encore' ? 'white' : '#6b7280', border: '#e5e7eb' },
                  ].map(({ key, nb, label, bg, color, border }) => (
                    <button key={key} onClick={() => setVeilleFilter(veilleFilter === key ? null : key)}
                      title={`Filtrer : ${label}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, border: `1px solid ${border}`, background: bg, color, fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .15s' }}>
                      <span style={{ fontSize: 16, lineHeight: 1 }}>{nb}</span>
                      <span style={{ fontSize: 11, fontWeight: 500 }}>{label}{nb > 1 ? 's' : ''}</span>
                    </button>
                  ))}
                  {veilleFilter && (
                    <button onClick={() => setVeilleFilter(null)}
                      style={{ padding: '5px 10px', borderRadius: 20, border: '1px solid #e5e7eb', background: 'white', color: '#6b7280', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
                      ✕ Tout afficher
                    </button>
                  )}
                </div>
              </div>
              {veilleFilter && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f3f4f6', fontSize: 11, color: '#6b7280' }}>
                  Le tableau ci-dessous est filtré — <strong style={{ color: '#374151' }}>{employesFiltres.length} employé{employesFiltres.length > 1 ? 's' : ''}</strong> affiché{employesFiltres.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}

          {/* Modal ajout rapide absence */}
          {absModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setAbsModal(null)}>
              <div style={{ background: 'white', borderRadius: 14, padding: '20px 24px', width: 440 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>Absence — {absModal.nom}</div>
                  <button onClick={() => setAbsModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color="#6b7280" /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                  {[['Du', 'date_debut', 'date'], ['Au', 'date_fin', 'date']].map(([lbl, k, t]) => (
                    <div key={k}><div style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>{lbl}</div>
                      <input type={t} value={absForm[k]} onChange={e => setAbsForm(f => ({ ...f, [k]: e.target.value }))}
                        style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', marginTop: 4, boxSizing: 'border-box' }} /></div>
                  ))}
                  <div><div style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>Type</div>
                    <select value={absForm.type} onChange={e => setAbsForm(f => ({ ...f, type: e.target.value }))}
                      style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', marginTop: 4, boxSizing: 'border-box' }}>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>Commentaire</div>
                  <input value={absForm.commentaire} onChange={e => setAbsForm(f => ({ ...f, commentaire: e.target.value }))} placeholder="Optionnel"
                    style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', marginTop: 4, boxSizing: 'border-box' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setAbsModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'white', cursor: 'pointer', fontSize: 13 }}>Annuler</button>
                  <button onClick={handleAddAbsence} disabled={absSaving || !absForm.date_debut || !absForm.date_fin}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--blue)', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 13, opacity: absSaving ? 0.6 : 1 }}>
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* KPIs du jour */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            {[
              { val: nbPresents,          label: 'Présents aujourd\'hui', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', Icon: UserCheck },
              { val: nbConge,             label: 'En congé',              color: '#d97706', bg: '#fef3c7', border: '#fde68a', Icon: Umbrella },
              { val: nbMaladie,           label: 'En maladie',            color: '#dc2626', bg: '#fee2e2', border: '#fecaca', Icon: BriefcaseMedical },
              { val: nbInactifs,          label: 'Inactifs',              color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb', Icon: User },
            ].map(k => (
              <div key={k.label} style={{ ...card, background: k.bg, border: `1px solid ${k.border}`, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <k.Icon size={20} color={k.color} />
                </div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.val}</div>
                  <div style={{ fontSize: 11, color: k.color, opacity: 0.8, marginTop: 3 }}>{k.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Tableau statut employés */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                Statut des employés — aujourd'hui
                {veilleFilter && <span style={{ fontWeight: 400, fontSize: 12, color: '#6b7280', marginLeft: 8 }}>({employesFiltres.length} résultat{employesFiltres.length > 1 ? 's' : ''})</span>}
              </div>
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher par nom ou service…"
                style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '7px 14px', fontSize: 13, width: 240, outline: 'none' }}
              />
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Employé', 'Service', 'Statut', veille.length > 0 ? 'Badgeage' : null, 'Absence en cours', 'Prochaine absence', ''].filter(Boolean).map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 0', fontSize: 11, color: 'var(--gray)', fontWeight: 500, paddingRight: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employesFiltres.map(e => {
                  const absEnCours = allAbsences.filter(a => isActiveAbs(a) && a.employe_id === e.id);
                  const absFuture  = allAbsences.filter(a => isFutureAbs(a) && a.employe_id === e.id).sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut))[0];
                  const statutLabel = e.statut === 'inactif' ? { label: 'Inactif', bg: '#f3f4f6', color: '#6b7280' }
                    : e.en_conge ? (absEnCours[0]?.type === 'maladie' ? { label: 'Maladie', bg: '#fee2e2', color: '#dc2626' } : { label: 'En congé', bg: '#fef3c7', color: '#d97706' })
                    : { label: 'Présent', bg: '#f0fdf4', color: '#16a34a' };
                  const vData = veilleById[e.id];
                  const vBadge = vData ? (
                    vData.heure_depart
                      ? <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500 }}>{vData.heure_arrivee?.slice(0,5)} → {vData.heure_depart?.slice(0,5)}</span>
                      : vData.heure_arrivee
                        ? <span style={{ fontSize: 11, color: '#1a56db', fontWeight: 500 }}>↳ {vData.heure_arrivee?.slice(0,5)}</span>
                        : <span style={{ fontSize: 11, color: '#d1d5db' }}>—</span>
                  ) : null;

                  return (
                    <tr key={e.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ padding: '11px 0', paddingRight: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>{e.initiales}</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{e.nom}</div>
                            <div style={{ fontSize: 11, color: 'var(--gray)' }}>{e.matricule || `#${e.id}`}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#374151', paddingRight: 12 }}>{e.denomination || e.service || '—'}</td>
                      <td style={{ paddingRight: 12 }}>
                        <span style={{ background: statutLabel.bg, color: statutLabel.color, fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 10 }}>{statutLabel.label}</span>
                      </td>
                      {veille.length > 0 && <td style={{ paddingRight: 12 }}>{vBadge}</td>}
                      <td style={{ paddingRight: 12 }}>
                        {absEnCours.length > 0 ? absEnCours.map(a => (
                          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 11, ...TYPE_COLORS[a.type] && { color: TYPE_COLORS[a.type].color } }}>{TYPE_LABELS[a.type]} — {fmt(a.date_debut)} → {fmt(a.date_fin)}</span>
                            <button onClick={() => handleDeleteAbsence(e.id, a.id, TYPE_LABELS[a.type])} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><Trash2 size={11} color="#dc2626" /></button>
                          </div>
                        )) : <span style={{ fontSize: 11, color: 'var(--gray)' }}>—</span>}
                      </td>
                      <td style={{ paddingRight: 12 }}>
                        {absFuture ? (
                          <span style={{ fontSize: 11, color: '#6b7280' }}>{TYPE_LABELS[absFuture.type]} — {fmt(absFuture.date_debut)} → {fmt(absFuture.date_fin)}</span>
                        ) : <span style={{ fontSize: 11, color: 'var(--gray)' }}>—</span>}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:5 }}>
                          <button onClick={() => setAbsListModal(e)}
                            title="Absences" style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${e.en_conge ? '#fde68a' : 'var(--border)'}`, background: e.en_conge ? '#fef3c7' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <CalendarOff size={13} color={e.en_conge ? '#d97706' : '#374151'} />
                          </button>
                          <button onClick={() => setHeuresModal(e)}
                            title="Gestion des heures" style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #bfdbfe', background: '#eff6ff', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <Clock size={13} color="#1a56db" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {employesFiltres.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray)', fontSize: 13 }}>{employes.length === 0 ? 'Aucun employé enregistré' : 'Aucun résultat'}</div>}
          </div>

        </div>
      )}

      {/* ── EXPORTS ── */}
      {activeTab === 'export' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {[
            { Icon: User,  title: 'Employés', desc: 'Export complet avec coordonnées, services et statuts.', route: 'employes', file: 'employes' },
            { Icon: Users, title: 'Clients / Patients', desc: 'Export complet avec séjours, contacts et consentements.', route: 'clients', file: 'clients' },
          ].map(e => (
            <div key={e.title} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 12, background: 'var(--blue-light)' }}><e.Icon size={24} color="var(--blue)" /></div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{e.title}</div>
              <div style={{ fontSize: 13, color: 'var(--gray)', flex: 1 }}>{e.desc}</div>
              <button onClick={() => exportCSV(e.route, `${e.file}_${new Date().toISOString().slice(0,10)}.csv`)}
                style={{ background: 'var(--blue)', color: 'white', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Download size={14} /> Télécharger CSV
              </button>
            </div>
          ))}
        </div>
      )}
      {confirmModal}
    </div>
  );
}
